<script setup lang="ts">

import {ref, computed, inject} from 'vue';
import {SvgIcon} from '../../../svg.ts';

import FileTreeItem from '../FileTreeItem.vue';
import FileTreeHierarchyGroup from './FileTreeHierarchyGroup.vue';
import {FILE_DIFF_STATUS, isContainerNode, LOADING_STATUS, type ContainerNode, type ProjectPageDiff, type TreeNode} from '../../types/file_tree.ts';
import type {Store} from '../../store.ts';
import {shouldShowLeaf, statusClasses, statusIcon} from './utils.ts';
const {icon, item, forceExpand = false} = defineProps<{
  icon: string,
  item: ContainerNode,
  forceExpand?: boolean,
}>();

const collapsed = ref(false);
const isExpanded = computed(() => forceExpand || !collapsed.value);
const store = inject<Store>('store');
const locale = inject('locale', {hierarchyLoadFailed: ''});
const isHierarchyLoading = computed(() => item.ty === 'project-group' && item.loadingStatus === LOADING_STATUS.IN_PROGRESS);

const showHierarchyLoadWarning = computed(() => {
  if (item.ty !== 'project-group' || !item.hierarchyLoadFailed) return false;

  const represented = new Set((item.hierarchyPages ?? []).map((page) => page.nameHash));
  return item.children?.some((child) =>
    child.ty === 'bucket' &&
    child.name === 'Source Documents' &&
    isContainerNode(child) &&
    child.children.some(
      (document) => document.ty === 'sch-diff' && !represented.has(document.nameHash),
    ),
  ) ?? false;
});

const hierarchyPagesById = computed(() => {
  const pages = item.ty === 'project-group' || item.ty === 'bucket' ? item.hierarchyPages ?? [] : [];
  return new Map<string, ProjectPageDiff>(pages.map((page) => [page.pageId, page]));
});

const visibleChildren = computed((): TreeNode[] => {
  // The backend serializes empty children with omitempty, so a container can
  // arrive without a children key.
  const children = item.children ?? [];
  if (item.ty !== 'project-group') {
    return children;
  }

  // A document the hierarchy represents renders as hierarchy rows instead of
  // its flat row. One the hierarchy does not represent keeps its flat row —
  // hiding it would drop the document from the tree entirely.
  const represented = new Set((item.hierarchyPages ?? []).map((page) => page.nameHash));
  return children.map((child) => {
    if (child.ty !== 'bucket' || child.name !== 'Source Documents') {
      return child;
    }
    return {
      ...child,
      children: child.children.filter((document) =>
        !('nameHash' in document) || !represented.has(document.nameHash)),
      hierarchy: item.hierarchy,
      hierarchyPages: item.hierarchyPages,
    };
  });
});

function nodeWillRender(node: TreeNode): boolean {
  if (isContainerNode(node)) {
    return (node.children ?? []).some(nodeWillRender);
  }
  if (node.ty === 'multi-page-diff' || node.ty === 'proj-diff') {
    return true;
  }
  if (!('status' in node) || !('nameHash' in node)) {
    return true;
  }

  const showUnchanged = (node.ty === 'sch-diff' || node.ty === 'proj-page-diff') &&
    store?.diffFilterConfig.showUnchangedSheets.value === true;
  return shouldShowLeaf(node.status, node.nameHash, store?.activeHash.value ?? '', showUnchanged);
}

const isVisible = computed((): boolean => {
  if (item.ty !== 'bucket') return true;
  if (visibleChildren.value.some(nodeWillRender)) return true;

  return (item.hierarchyPages ?? []).some(nodeWillRender);
});

const projectHasChanges = computed(() => item.ty === 'project-group' && item.status !== FILE_DIFF_STATUS.UNCHANGED);

function toggleCollapsed(): void {
  collapsed.value = !collapsed.value;
}

function toggleContainerRow(): void {
  if (item.ty !== 'project-group') {
    toggleCollapsed();
  }
}
</script>

<template>
  <template v-if="isVisible">
    <div
      class="tw:flex tw:gap-1 tw:p-1 tw:items-center tw:hover:cursor-pointer tw:hover:bg-hover"
      :title="item.headPath"
      :class="{'tw:text-text tw:bg-active tw:rounded-[4px]': item.ty === 'project-group' && item.nameHash === store?.activeHash.value}"
      @click.stop="toggleContainerRow"
    >
      <div
        data-test="file-tree-toggle"
        @click.stop="toggleCollapsed"
      >
        <SvgIcon :name="isExpanded ? 'octicon-chevron-down' : 'octicon-chevron-right'" />
      </div>
      <a
        v-if="item.ty === 'project-group'"
        :href="`#diff-${item.nameHash}`"
        data-test="project-group-link"
        class="tw:flex tw:items-center tw:gap-1 tw:text-text tw:no-underline tw:grow"
      >
        <SvgIcon
          class="text primary"
          :name="icon"
        />
        <span class="tw:text-ellipsis tw:overflow-hidden tw:whitespace-nowrap">{{ item.name }}</span>
        <div
          v-if="isHierarchyLoading"
          class="is-loading tw:h-8 tw:ml-auto tw:mr-4"
        />
        <SvgIcon
          v-else-if="projectHasChanges"
          :name="statusIcon(item.status)"
          data-test="project-change-indicator"
          class="tw:ml-auto"
          :class="statusClasses(item.status)"
          title="Project file changed"
        />
      </a>
      <template v-else>
        <SvgIcon
          class="text primary"
          :name="icon"
        />
        <span class="tw:text-ellipsis tw:overflow-hidden tw:whitespace-nowrap">{{ item.name }}</span>
      </template>
    </div>
    <!-- Children -->
    <div
      v-if="!isHierarchyLoading"
      v-show="isExpanded"
      data-test="file-tree-children"
      class="tw:ml-3 tw:border-l tw:pl-3 tw:border-secondary"
    >
      <div
        v-if="showHierarchyLoadWarning"
        class="ui tiny warning message tw:my-1"
      >
        {{ locale.hierarchyLoadFailed }}
      </div>
      <template v-if="item.ty === 'bucket' && item.hierarchy?.length">
        <FileTreeHierarchyGroup
          v-for="group in item.hierarchy"
          :key="group.groupId"
          :group="group"
          :children-by-page-id="hierarchyPagesById"
          :force-expand="forceExpand"
        />
      </template>
      <FileTreeItem
        v-for="child in visibleChildren"
        :key="child.headPath"
        :item="child"
        :force-expand="forceExpand"
      />
    </div>
  </template>
</template>
